import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { loginApi } from '@/lib/auth/authApi';
import { safeInternalPath } from '@/lib/url-params';
import { useAuthStore } from '@/stores/auth-store';
import { CircleDot, Eye, EyeOff, Lock, Mail } from 'lucide-react';

import MfaChallengeScreen from './MfaChallengeScreen';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, setChallenge, clearChallenge, pendingChallenge } = useAuthStore();

  // SSO : fromSource=site → après login, redirige vers le front avec le token
  const fromSource = searchParams.get('fromSource');
  const redirectTo = safeInternalPath(searchParams.get('redirectTo'));

  const oauthError = searchParams.get('error');
  const oauthErrorMessage =
    oauthError === 'registration_closed'
      ? 'Les inscriptions sont actuellement fermées.'
      : oauthError === 'oauth_failed'
        ? 'La connexion via Google a échoué. Veuillez réessayer.'
        : oauthError === 'oauth_error'
          ? 'Une erreur est survenue lors de la connexion. Veuillez réessayer.'
          : null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(oauthErrorMessage);

  function handleMfaSuccess() {
    clearChallenge();
    if (fromSource === 'site') {
      const store = useAuthStore.getState();
      const token = store.accessToken;
      const frontUrl =
        (import.meta.env.VITE_URL_FRONT as string | undefined)?.replace(/\/$/, '') || 'http://localhost:5232';
      window.location.href = `${frontUrl}/auth/callback#token=${token}&redirectTo=${encodeURIComponent(redirectTo)}`;
    } else {
      navigate(redirectTo || ROUTES.ROOT, { replace: true });
    }
  }

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const data = await loginApi({ email, password });

      if ('requiresMfa' in data && data.requiresMfa) {
        setChallenge({
          token: data.challengeToken,
          type: data.challengeType,
          methodsAllowed: data.methodsAllowed,
          obfuscatedEmail: data.obfuscatedEmail,
        });
        return;
      }

      // Login direct sans MFA
      login(data.user, data.accessToken);

      if (fromSource === 'site') {
        const frontUrl =
          (import.meta.env.VITE_URL_FRONT as string | undefined)?.replace(/\/$/, '') || 'http://localhost:5232';
        window.location.href = `${frontUrl}/auth/callback#token=${data.accessToken}&redirectTo=${encodeURIComponent(redirectTo)}`;
      } else {
        navigate(redirectTo || ROUTES.ROOT, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la connexion');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Étape 2 : challenge MFA
  if (pendingChallenge) {
    return <MfaChallengeScreen challenge={pendingChallenge} onSuccess={handleMfaSuccess} onCancel={clearChallenge} />;
  }

  // Inscriptions fermées (redirigé depuis OAuth callback)
  if (oauthError === 'registration_closed') {
    return (
      <section className='min-h-screen bg-gradient-to-br from-emerald-600 to-emerald-400 flex flex-col items-center justify-center px-6'>
        <div className='flex items-center gap-3 mb-8'>
          <CircleDot className='w-10 h-10 text-white' />
          <span className='text-2xl font-extrabold text-white'>BoulesConnect</span>
        </div>
        <div className='w-full max-w-md bg-white rounded-3xl p-8 shadow-xl text-center'>
          <div className='w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4'>
            <Lock className='w-8 h-8 text-amber-600' />
          </div>
          <h2 className='text-2xl font-extrabold text-slate-900 mb-2'>Inscriptions fermées</h2>
          <p className='text-slate-500 text-sm mb-6'>
            Les inscriptions sont temporairement fermées. Veuillez réessayer ultérieurement.
          </p>
          <Link
            to={ROUTES.LOGIN}
            className='inline-block bg-emerald-600 text-white font-bold px-6 py-3 rounded-xl hover:bg-emerald-700 transition-colors'
          >
            Se connecter
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className='min-h-screen bg-gradient-to-br from-emerald-900 to-emerald-500  flex flex-col items-center justify-center px-6 pt-20 pb-10'>
      {/* Brand */}
      <div className='flex items-center gap-3 mb-8'>
        <CircleDot className='w-10 h-10 text-white' />
        <span className='text-2xl font-extrabold text-white'>BoulesConnect</span>
      </div>

      {/* Card */}
      <div className='w-full max-w-md bg-white rounded-3xl p-8 shadow-xl'>
        <h2 className='text-2xl font-extrabold text-slate-900 text-center mb-8'>Se connecter</h2>

        {/* Error banner */}
        {error && (
          <div className='mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700'>{error}</div>
        )}

        <form onSubmit={handleSubmit} className='space-y-5'>
          {/* Email */}
          <div>
            <label htmlFor='email' className='block text-sm font-semibold text-slate-700 mb-2'>
              Adresse email
            </label>
            <div className='relative'>
              <Mail className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400' />
              <input
                id='email'
                type='email'
                required
                maxLength={255}
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder='votre@email.com'
                className='w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors'
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor='password' className='block text-sm font-semibold text-slate-700 mb-2'>
              Mot de passe
            </label>
            <div className='relative'>
              <Lock className='absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400' />
              <input
                id='password'
                type={showPassword ? 'text' : 'password'}
                required
                maxLength={128}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder='Votre mot de passe'
                className='w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-colors'
              />
              <button
                type='button'
                onClick={() => setShowPassword(!showPassword)}
                className='absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors'
              >
                {showPassword ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
              </button>
            </div>
          </div>

          {/* Forgot */}
          <div className='flex items-center justify-end'>
            <Link to={ROUTES.FORGOT_PASSWORD} className='text-sm text-emerald-600 font-semibold hover:underline'>
              Mot de passe oublié ?
            </Link>
          </div>

          {/* Submit */}
          <button
            type='submit'
            disabled={isSubmitting}
            className='w-full bg-emerald-600 text-white cursor-pointer font-bold py-3 rounded-xl hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors'
          >
            {isSubmitting ? 'Connexion…' : 'Se connecter'}
          </button>
        </form>

        {/* OAuth social */}
        <div className='mt-6'>
          <div className='relative mb-5'>
            <div className='absolute inset-0 flex items-center'>
              <div className='w-full border-t border-slate-200' />
            </div>
            <div className='relative flex justify-center text-xs'>
              <span className='bg-white px-3 text-slate-400'>ou</span>
            </div>
          </div>

          <a
            href={`${import.meta.env.VITE_URL_API}/v1/auth/google?callbackUrl=${encodeURIComponent(window.location.origin + '/auth/callback')}`}
            className='flex w-full items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50'
          >
            <svg className='h-5 w-5' viewBox='0 0 24 24'>
              <path
                d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z'
                fill='#4285F4'
              />
              <path
                d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                fill='#34A853'
              />
              <path
                d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z'
                fill='#FBBC05'
              />
              <path
                d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                fill='#EA4335'
              />
            </svg>
            Continuer avec Google
          </a>
        </div>

        {/* Register link */}
        <p className='text-center text-sm text-slate-500 mt-6'>
          Pas de compte ?{' '}
          <Link
            to={`${ROUTES.REGISTER}${fromSource === 'site' ? `?fromSource=site&redirectTo=${encodeURIComponent(redirectTo)}` : ''}`}
            className='text-emerald-600 font-semibold hover:underline'
          >
            S&apos;inscrire
          </Link>
        </p>
      </div>

      {/* Footer */}
      <div className='flex items-center gap-4 mt-8 text-sm text-white/70'>
        <Link
          to={`${import.meta.env.VITE_URL_FRONT}${ROUTES.FRONT_SUPPORT}`}
          className='hover:text-white transition-colors'
        >
          Aide
        </Link>
        <span>&middot;</span>
        <a
          href={`${import.meta.env.VITE_URL_FRONT}${ROUTES.FRONT_POLITIQUE_RGPD}`}
          className='hover:text-white transition-colors'
        >
          Confidentialité
        </a>
        <span>&middot;</span>
        <Link
          to={`${import.meta.env.VITE_URL_FRONT}${ROUTES.FRONT_CGU_CGV}`}
          className='hover:text-white transition-colors'
        >
          Conditions
        </Link>
      </div>
    </section>
  );
}
